import { ScanTableData, FindingsGroup } from "../../models/tables";
import { CommonFilterData, OptionItem } from "../../models/filters"
import { Table, getFindingValueForSorting } from "../utils";
import { scansPageSize } from "../config";

export class ScanTable extends Table {

    isCodebaseFiltered: boolean = false;
    isCodebase: boolean = false;
    displayedScans: { [key: string]: boolean } = {};
    scansInProject: { [key: string]: string[] } = {};
    openCodebases: { [key: string]: boolean } = {};
    collapsedCodebase: { [key: string]: boolean } = {};
    pageSize: number = scansPageSize;


    sort(col: string) {
        if (this.sortCol === col) {
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortAsc = false;
        }
        this.sortCol = col;

        if (this.sortCol !== "") {
            if (this.isCodebaseFiltered) {
                // Direct sort without grouping
                this.tableData.sort((a :ScanTableData , b: ScanTableData) => this.compareValues(a, b, col));
            } else {
                // Step 1: Group data by project and sort within each group
                const groupedData = this.groupByProject(this.tableData);

                // Step 2: Sort each project group based on the selected column
                Object.values(groupedData).forEach(group => {
                    group.sort((a, b) => this.compareValues(a, b, col));
                });

                const sortedGroups = Object.keys(groupedData)
                .sort((projectIdA, projectIdB) => {
                    const firstItemA = groupedData[projectIdA][0];
                    const firstItemB = groupedData[projectIdB][0];
                    return this.compareValues(firstItemA, firstItemB, col);
                })
                .map(projectId => groupedData[projectId]);


                // Step 3: Flatten grouped data back to an array, preserving project grouping
                this.tableData = sortedGroups.flat();
            }
        }
        this.firstPage()
    }

    compareValues(a: ScanTableData, b: ScanTableData, col: string): number {

        if (col === "project") {
            if (!a.project) return 1;
            if (!b.project) return -1;
            if (a.project.name < b.project.name) return this.sortAsc ? 1 : -1;
            if (a.project.name > b.project.name) return this.sortAsc ? -1 : 1;
        } else if (col === "user") {
            if (!a.user) return 1;
            if (!b.user) return -1;
            if (a.user?.name < b.user?.name) return this.sortAsc ? 1 : -1;
            if (a.user?.name > b.user?.name) return this.sortAsc ? -1 : 1;
        } else if (col === "findings") {
            const aValue = getFindingValueForSorting(a.findings);
            const bValue = getFindingValueForSorting(b.findings);
            if (aValue < bValue) return this.sortAsc ? 1 : -1;
            if (aValue > bValue) return this.sortAsc ? -1 : 1;
        } else if (a[this.sortCol as keyof ScanTableData] != null && b[this.sortCol as keyof ScanTableData] != null) {
            const aValue = a[this.sortCol as keyof ScanTableData] ?? "";
            const bValue = b[this.sortCol as keyof ScanTableData] ?? "";

            if (aValue < bValue) return this.sortAsc ? 1 : -1;
            if (aValue > bValue) return this.sortAsc ? -1 : 1;
        }


        if (a[this.sortCol as keyof ScanTableData]! < b[this.sortCol as keyof ScanTableData]!) {
            return this.sortAsc ? 1 : -1;
        }
        if (a[this.sortCol as keyof ScanTableData]! > b[this.sortCol as keyof ScanTableData]!) {
            return this.sortAsc ? -1 : 1;
        }

        return this.sortAsc ? -1 : 1;
    }

    // Helper function to group data by project ID
    groupByProject(data: ScanTableData[]): Record<string, ScanTableData[]> {
        return data.reduce((acc: Record<string, ScanTableData[]>, scan: ScanTableData) => {
            const projectId = scan.project?.id || "undefined";
            if (!acc[projectId]) {
                acc[projectId] = [];
            }
            acc[projectId].push(scan);
            return acc;
        }, {});
    }

    defaultSorting() {
        this.tableData.sort((a: ScanTableData, b: ScanTableData) => b.date - a.date);
    }

    filterTable(filterData: CommonFilterData[]) {
        if(!this.isCodebase){
            this.isCodebaseFiltered = false;
        }
        let tableDataCopy: ScanTableData[] = [...this.constantTableData];

        filterData.forEach((filterItem: CommonFilterData) => {
            const validOptions: OptionItem[] = filterItem.options.filter((optionItem: OptionItem) => optionItem.checked);

            if (validOptions.length === 0) return;

            tableDataCopy = tableDataCopy.filter((item: ScanTableData) => !!validOptions.find((option: OptionItem) => {
                if (item.project && filterItem.col === 'project') {
                    this.isCodebaseFiltered = true;
                    return option.value === item.project.name;
                }
                if (item.branch_name && filterItem.col === 'branch_name') {
                    return option.value === item.branch_name;
                }
                if (filterItem.col === 'findings') {
                    return (item.findings.groups[option.value as keyof FindingsGroup] || 0) > 0
                }
                if (filterItem.type === 'Date Range') {
                    return new Date(item.date * 1000) > new Date(option.from || "") && new Date(item.date * 1000) < new Date(option.to || "")
                }
                else return option.value === item[filterItem.col as keyof ScanTableData]
            }))
        });
        this.tableData = tableDataCopy;
        // Reset current page number if filtered table data length is smaller than page size
        if (this.tableData.length < this.pageSize) {
            this.curPage = 1;
        }
    }


    pagedTableData() {
        if (this.tableData) {

            // Paginate the already sorted `tableData`
            let start = (this.curPage - 1) * this.pageSize;
            let end = this.curPage * this.pageSize;
            const paginatedData = this.tableData.slice(start, end);


            const res = this.prepareDisplayedScans(paginatedData);
            return res
        } else {
            return [];
        }
    }

    resetTableData(newData: any) {
        if(!this.isCodebase){
            this.isCodebaseFiltered = false;
        }
        this.tableData = this.constantTableData = newData;
        this.defaultSorting();
    }


    prepareDisplayedScans(scans: ScanTableData[]): ScanTableData[] {
        if(this.isCodebaseFiltered) {
            return scans
        }
        const groupedScans = this.groupByProject(scans);
        const scansInProject: Record<string, string[]> = {};
        const displayedScans: Record<string, boolean> = {};
        const finalDisplayList: ScanTableData[] = [];

        // Process each project group
        Object.values(groupedScans).forEach((projectGroup) => {
            projectGroup.forEach((item, index) => {
                const projectId = item.project.id;
                const pageId = `${this.curPage}_${projectId}`; // Combine page and projectId


                // Initialize the project group if it doesn't exist
                if (!scansInProject[projectId]) {
                    scansInProject[projectId] = [];
                }
                scansInProject[projectId].push(item.id);


                if (this.collapsedCodebase[pageId]) {
                    this.openCodebases[pageId] = false;
                } else if (item.findings.total > 0 && !this.openCodebases[pageId]) {
                    this.openCodebases[pageId] = true; // Auto-expand if findings exist and not collapsed
                }
                if (index === 0) {
                    displayedScans[item.id] = true;
                    finalDisplayList.push(item);
                } else if (this.openCodebases[pageId]) {
                    // Add additional scans if the project is open
                    finalDisplayList.push(item);
                }
            });
        });

        // Update the class properties for scansInProject and displayedScans
        this.scansInProject = scansInProject;
        this.displayedScans = displayedScans;
        return finalDisplayList; // Return the filtered list of scans
    }

    nextPage() {
        if (this.curPage * this.pageSize < this.tableData.length) this.curPage++;

    }

    previousPage() {
       if (this.curPage > 1) this.curPage--;
    }

    firstPage() {
        if(this.curPage > 1) this.curPage = 1
    }

    setIsCodebase() {
        this.isCodebase = true;
        this.isCodebaseFiltered = true;
    }
}
